// src/pages/cultivos/Cultivos.js
import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'

const ESTADO_BADGE = {
  planificado: 'badge-gray', sembrado: 'badge-blue',
  en_crecimiento: 'badge-green', cosechado: 'badge-purple', perdido: 'badge-red'
}
const ESTADO_LABEL = {
  planificado: '📋 Planificado', sembrado: '🌱 Sembrado',
  en_crecimiento: '🌿 En crecimiento', cosechado: '🌾 Cosechado', perdido: '💀 Perdido'
}

const EMPTY = {
  parcela_id: '', tipo_cultivo_id: '', fecha_siembra: '',
  fecha_cosecha_estimada: '', area_ha: '', insumos_costo: '',
  estado: 'planificado', rendimiento_kg: '', precio_kg: '', notas: ''
}

export default function Cultivos() {
  const { profile } = useAuth()
  const canWrite = ['admin', 'capataz'].includes(profile?.rol)

  const [cultivos, setCultivos] = useState([])
  const [parcelas, setParcelas] = useState([])
  const [tipos, setTipos] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [filtroEstado, setFiltroEstado] = useState('')

  useEffect(() => { loadData() }, [])

  async function loadData() {
    setLoading(true)
    const [cRes, pRes, tRes] = await Promise.all([
      supabase.from('cultivos').select('*, parcela:parcelas(nombre), tipo:tipos_cultivo(nombre)').order('created_at', { ascending: false }),
      supabase.from('parcelas').select('*').order('nombre'),
      supabase.from('tipos_cultivo').select('*').order('nombre'),
    ])
    setCultivos(cRes.data ?? [])
    setParcelas(pRes.data ?? [])
    setTipos(tRes.data ?? [])
    setLoading(false)
  }

  function openNew() { setForm(EMPTY); setEditId(null); setModal(true) }
  function openEdit(c) {
    setForm({
      parcela_id: c.parcela_id ?? '', tipo_cultivo_id: c.tipo_cultivo_id ?? '',
      fecha_siembra: c.fecha_siembra ?? '', fecha_cosecha_estimada: c.fecha_cosecha_estimada ?? '',
      area_ha: c.area_ha ?? '', insumos_costo: c.insumos_costo ?? '',
      estado: c.estado, rendimiento_kg: c.rendimiento_kg ?? '', precio_kg: c.precio_kg ?? '',
      notas: c.notas ?? ''
    })
    setEditId(c.id); setModal(true)
  }

  function onChange(e) { setForm(f => ({ ...f, [e.target.name]: e.target.value })) }

  async function save() {
    if (!form.tipo_cultivo_id) return alert('El tipo de cultivo es obligatorio')
    setSaving(true)
    const payload = { ...form, registrado_por: profile.id }
    if (editId) await supabase.from('cultivos').update(payload).eq('id', editId)
    else await supabase.from('cultivos').insert([payload])
    setSaving(false); setModal(false); loadData()
  }

  const filtered = cultivos.filter(c => !filtroEstado || c.estado === filtroEstado)

  const rentabilidad = (c) => {
    if (!c.rendimiento_kg || !c.precio_kg) return null
    const ingreso = c.rendimiento_kg * c.precio_kg
    const costo = c.insumos_costo ?? 0
    return ingreso - costo
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">🌾 Cultivos</div>
          <div className="page-subtitle">Gestión de siembras, cosechas y rendimiento</div>
        </div>
        {canWrite && <button className="btn btn-primary" onClick={openNew}>+ Nuevo cultivo</button>}
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <button className={`btn btn-sm ${!filtroEstado ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFiltroEstado('')}>Todos</button>
          {Object.entries(ESTADO_LABEL).map(([k, v]) => (
            <button key={k} className={`btn btn-sm ${filtroEstado === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFiltroEstado(k)}>{v}</button>
          ))}
        </div>
        <div className="table-wrap">
          {loading
            ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner spinner-dark" style={{ margin: 'auto' }} /></div>
            : (
              <table>
                <thead>
                  <tr>
                    <th>Cultivo</th>
                    <th>Parcela</th>
                    <th>Siembra</th>
                    <th>Cosecha est.</th>
                    <th>Área (ha)</th>
                    <th>Rendim. (kg)</th>
                    <th>Rentabilidad</th>
                    <th>Estado</th>
                    {canWrite && <th>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && <tr><td colSpan={9}><div className="empty-state">Sin cultivos</div></td></tr>}
                  {filtered.map(c => {
                    const rent = rentabilidad(c)
                    return (
                      <tr key={c.id}>
                        <td><strong>{c.tipo?.nombre}</strong></td>
                        <td>{c.parcela?.nombre ?? '—'}</td>
                        <td>{c.fecha_siembra ? new Date(c.fecha_siembra).toLocaleDateString('es') : '—'}</td>
                        <td>{c.fecha_cosecha_estimada ? new Date(c.fecha_cosecha_estimada).toLocaleDateString('es') : '—'}</td>
                        <td>{c.area_ha ?? '—'}</td>
                        <td>{c.rendimiento_kg ? `${Number(c.rendimiento_kg).toLocaleString()} kg` : '—'}</td>
                        <td>
                          {rent !== null
                            ? <span style={{ fontWeight: 600, color: rent >= 0 ? 'var(--brand-600)' : 'var(--accent-red)' }}>
                                ${rent.toLocaleString('es', { maximumFractionDigits: 0 })}
                              </span>
                            : '—'}
                        </td>
                        <td><span className={`badge ${ESTADO_BADGE[c.estado]}`}>{c.estado}</span></td>
                        {canWrite && (
                          <td><button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}>✏️</button></td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )
          }
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div className="modal-title">{editId ? 'Editar cultivo' : 'Nuevo cultivo'}</div>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(false)}>✕</button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Tipo de cultivo *</label>
                <select className="form-select" name="tipo_cultivo_id" value={form.tipo_cultivo_id} onChange={onChange}>
                  <option value="">Seleccionar...</option>
                  {tipos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Parcela</label>
                <select className="form-select" name="parcela_id" value={form.parcela_id} onChange={onChange}>
                  <option value="">Sin parcela asignada</option>
                  {parcelas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Fecha de siembra</label>
                <input className="form-input" type="date" name="fecha_siembra" value={form.fecha_siembra} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Cosecha estimada</label>
                <input className="form-input" type="date" name="fecha_cosecha_estimada" value={form.fecha_cosecha_estimada} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Área (hectáreas)</label>
                <input className="form-input" type="number" step="0.01" name="area_ha" value={form.area_ha} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Costo insumos ($)</label>
                <input className="form-input" type="number" step="0.01" name="insumos_costo" value={form.insumos_costo} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Rendimiento real (kg)</label>
                <input className="form-input" type="number" name="rendimiento_kg" value={form.rendimiento_kg} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Precio por kg ($)</label>
                <input className="form-input" type="number" step="0.0001" name="precio_kg" value={form.precio_kg} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" name="estado" value={form.estado} onChange={onChange}>
                  {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notas</label>
              <textarea className="form-textarea" name="notas" value={form.notas} onChange={onChange} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
